import { useQuery } from '@tanstack/react-query';

import axiosClient from './axiosClient';

/**
 * WAVE G — ops overview of every tenant's authoring home env + drift flag.
 * GET /api/admin/editing-env-map (super-admin; backed by mig-214 view).
 *
 * Graceful: when the backend view isn't present (`available: false`) or the
 * route 404s (feature not deployed), the hook returns an empty list and the
 * page renders a "not available here" notice instead of erroring.
 */
export interface EditingEnvMapRow {
  tenantCode: string;
  tenantName: string | null;
  homeEnv: string;
  isDefaultHome: boolean;
  migratingTo: string | null;
  homeLockedAt: string | null;
  prevHomeEnv: string | null;
  homeSetBy: string | null;
  homeSetAt: string | null;
  currentVersion: string | null;
  isPartial: boolean;
  lastPublishedAt: string | null;
  drift: boolean;
}

export interface EditingEnvMapResponse {
  currentEnv: string;
  available: boolean;
  rows: EditingEnvMapRow[];
}

function isRouteAbsent(err: unknown): boolean {
  const status = (err as { response?: { status?: number } } | undefined)?.response?.status;
  return status === 404 || status === 501;
}

export function useEditingEnvMap(): {
  data: EditingEnvMapResponse;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery<EditingEnvMapResponse, unknown>({
    queryKey: ['editing-env-map'],
    queryFn: async () => {
      const { data } = await axiosClient.get<EditingEnvMapResponse>('/api/admin/editing-env-map');
      return data;
    },
    staleTime: 30_000,
    retry: (failureCount, err) => {
      if (isRouteAbsent(err)) return false;
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 1;
    },
  });

  // Degrade a missing route to available:false so the page shows the notice.
  if (isRouteAbsent(query.error)) {
    return { data: { currentEnv: '', available: false, rows: [] }, isLoading: false, isError: false };
  }

  return {
    data: query.data ?? { currentEnv: '', available: false, rows: [] },
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export default useEditingEnvMap;
